import { useEffect, useMemo, useState } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { z } from 'zod';
import { firestore } from '../firebase';
import catalogue from 'data/machineCatalogue.json';

export const manualCapacityInput = z.object({
  solutionTankLiters: z.number().finite().positive().max(10000),
  note: z.string().trim().min(1).max(1000),
});
const manualCapacityDocument = manualCapacityInput.extend({
  version: z.number().int().positive(),
  updatedBy: z.string().min(1),
  updatedAt: z.instanceof(firebase.firestore.Timestamp),
}).strict();
export type ManualCapacity = z.infer<typeof manualCapacityDocument>;
export type CatalogueMachine = typeof catalogue[number] & { manualCapacity?: ManualCapacity };
const collection = () => firestore.collection('machineCatalogueCapacities');

export function mergeManualCapacities(overrides: Record<string, ManualCapacity>): CatalogueMachine[] {
  return catalogue.map((machine) => {
    const manualCapacity = machine.solutionTankLiters == null ? overrides[machine.id] : undefined;
    return manualCapacity
      ? { ...machine, solutionTankLiters: manualCapacity.solutionTankLiters, manualCapacity }
      : machine;
  });
}

export async function saveManualCapacity(
  id: string, input: z.infer<typeof manualCapacityInput>, expectedVersion: number
) {
  const value = manualCapacityInput.parse(input);
  const machine = catalogue.find((entry) => entry.id === id);
  if (!machine || machine.solutionTankLiters != null) {
    throw new Error('Kun modeller uden en kildeverificeret kapacitet kan suppleres her.');
  }
  const uid = firebase.auth().currentUser?.uid;
  if (!uid) throw new Error('Log ind igen for at gemme.');
  const ref = collection().doc(id);
  await firestore.runTransaction(async (transaction) => {
    const snapshot = await transaction.get(ref);
    const current = snapshot.exists ? manualCapacityDocument.parse(snapshot.data()) : undefined;
    if ((current?.version ?? 0) !== expectedVersion) {
      throw new Error('Kapaciteten er ændret af en anden. Luk og åbn formularen igen.');
    }
    transaction.set(ref, {
      ...value,
      version: expectedVersion + 1,
      updatedBy: uid,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
  });
}

export default function useMachineCatalogue() {
  const [overrides, setOverrides] = useState<Record<string, ManualCapacity>>({});
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => collection().onSnapshot({ includeMetadataChanges: true }, (snapshot) => {
    const values: Record<string, ManualCapacity> = {};
    let invalid = false;
    snapshot.docs.forEach((doc) => {
      const parsed = manualCapacityDocument.safeParse(doc.data());
      if (parsed.success) values[doc.id] = parsed.data;
      else invalid = true;
    });
    setOverrides(values);
    setReady(!snapshot.metadata.fromCache && !invalid);
    setError(invalid ? 'Nogle gemte kapaciteter har ugyldige data og bruges ikke.' : '');
  }, () => {
    setReady(false);
    setError('Gemte kapaciteter kunne ikke hentes. Kontrollér forbindelse og adgang, og genindlæs siden.');
  }), []);
  const machines = useMemo(() => mergeManualCapacities(overrides), [overrides]);
  return { machines, ready, error };
}
